import { useContext, useEffect, useRef } from "react";
import useAsyncEffect from "use-async-effect";
import "./App.scss";
import { MiniMap } from "./Map/Map";
import { MenuBottom } from "./Menu/MenuBottom";
import { MenuLeft } from "./Menu/MenuLeft";
import { MenuPopup } from "./Menu/MenuPopup";
import { MenuTop } from "./Menu/MenuTop";
import { AboutGamePopup } from "./Popups/AboutGamePopup";
import { GeneratePopup } from "./Popups/GeneratePopup";
import { GenerationFinishedPopup } from "./Popups/GenerationFinishedPopup";
import { GlobalChatPopup } from "./Popups/GlobalChatPopup";
import { SearchPopup } from "./Popups/SearchPopup";
import { LoginPopup } from "./Popups/LoginPopup";
import { NotificationsPopup } from "./Popups/NotificationsPopup";
import { PlayerInfoPopup } from "./Popups/PlayerInfoPopup";
import { PlayerProfilePopup } from "./Popups/PlayerProfilePopup";
import { RarityRatingPopup } from "./Popups/RarityRatingPopup";
import { RedeemPopup } from "./Popups/RedeemPopup";
import { disconnectSocket, initSocket, socket } from "./Sockets/Sockets";
import { AppContext } from "./State/Context";
import { ActionType } from "./State/State";
import { SystemMessage } from "./SystemMessage/SystemMessage";
import { World } from "./World/World";
import { ZoomController } from "./Zoom/Zoom";
import { Statistic } from "./types";
import { RequestMethod, askAPI, showSystemMessage } from "./api/api";
import { CardPopup } from "./Popups/CardPopup";
import { requestHexagenInfo } from "./api/hexagen";
import { BattleFieldPopup } from "./Popups/BattleFieldPopup";
import { ReportPopup } from "./Popups/ReportPopup";
import { isCrawler } from "./utils/settings";
import { setVideoDebugEnabled, setHexDebugEnabled } from "./utils/videoDebugLog";
import { AdminPage } from "./Admin/AdminPage";

/** Rendered only when entry point detects pathname /admin; fetches player then shows Admin or redirect. */
export function AdminRoot() {
  const { state, dispatch } = useContext(AppContext);
  useAsyncEffect(async () => {
    const statistic = await askAPI<Statistic>("/api/statistic", RequestMethod.GET);
    if (statistic) {
      dispatch({ type: ActionType.UPDATE_STATISTIC, statistic });
      dispatch({ type: ActionType.UPDATE_PLAYER, player: statistic.player ?? null });
    }
  }, []);
  if (!state.player) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        Loading…
      </div>
    );
  }
  if (state.player.role !== "admin") {
    if (typeof window !== "undefined") window.location.href = "/";
    return null;
  }
  return <AdminPage />;
}

function App() {
  const { state, dispatch } = useContext(AppContext);
  const socketInitialized = useRef(false);
  const redditUserInitRef = useRef(false);
  const latestPlayerRef = useRef(state.player);
  latestPlayerRef.current = state.player;
  const urlHexCheckedRef = useRef<string | null>(null);

  useEffect(() => {
    const path = (typeof window !== "undefined" ? window.location.pathname : "").replace(/\/$/, "") || "";
    const m = path.match(/^\/hex\/(-?\d+)\/(-?\d+)\/view$/);
    if (!m || !state.selectedHexCoord) return;
    const x = parseInt(m[1], 10);
    const y = parseInt(m[2], 10);
    if (state.selectedHexCoord.x !== x || state.selectedHexCoord.y !== y) return;
    const key = `${x}-${y}`;
    if (urlHexCheckedRef.current === key) return;
    urlHexCheckedRef.current = key;
    let cancelled = false;
    requestHexagenInfo(x, y).then((exists) => {
      if (cancelled) return;
      if (exists) dispatch({ type: ActionType.OPEN_HEX_INFO_POPUP });
    });
    return () => {
      cancelled = true;
    };
  }, [state.selectedHexCoord, dispatch]);

  useEffect(() => {
    if (state.player && !socket.connected && !socketInitialized.current) {
      socketInitialized.current = true;
      initSocket();
    } else if (!state.player && socket.connected) {
      socketInitialized.current = false;
      disconnectSocket();
    }
  }, [state.player]);

  useEffect(() => {
    setVideoDebugEnabled(state.player?.role === "admin");
  }, [state.player?.role]);

  useEffect(() => {
    const onAdmin = state.player?.role === "admin";
    const fromUrl =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("hex_debug") === "1";
    setHexDebugEnabled(!!(onAdmin || fromUrl));
  }, [state.player?.role]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const unsubscribed = params.get("unsubscribed");
    const error = params.get("error");
    if (unsubscribed === "1") {
      showSystemMessage({ systemMessage: "success", text: "You are unsubscribed. Email sending is disabled for all players." });
      const params2 = new URLSearchParams(window.location.search);
      params2.delete("unsubscribed");
      window.history.replaceState({}, "", window.location.pathname + (params2.toString() ? "?" + params2.toString() : ""));
    } else if (error) {
      showSystemMessage({ systemMessage: "error", text: "This unsubscribe link is invalid or expired. Email sending is disabled for all players." });
      const params2 = new URLSearchParams(window.location.search);
      params2.delete("error");
      window.history.replaceState({}, "", window.location.pathname + (params2.toString() ? "?" + params2.toString() : ""));
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      if (typeof window === "undefined") return;
      if ((window as any).__searchClosingByBack) {
        (window as any).__searchClosingByBack = false;
        return;
      }
      if (
        window.location.pathname !== "/search" &&
        state.isSearchPopupOpen
      ) {
        dispatch({ type: ActionType.TOGGLE_SEARCH_POPUP });
        return;
      }
      window.location.reload();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [state.isSearchPopupOpen, dispatch]);

  useAsyncEffect(async () => {
    const fetchStatistic = async () => {
      const statistic = await askAPI<Statistic>(
        "/api/statistic",
        RequestMethod.GET
      );

      if (statistic) {
        const prevPlayer = latestPlayerRef.current;
        const newPlayer = statistic.player || null;

        dispatch({ type: ActionType.UPDATE_STATISTIC, statistic });

        dispatch({
          type: ActionType.UPDATE_NOTIFICATIONS,
          notifications: statistic.notifications || [],
        });

        dispatch({
          type: ActionType.UPDATE_PLAYER,
          player: newPlayer,
        });

        if (
          statistic.player &&
          !redditUserInitRef.current &&
          typeof window !== "undefined" &&
          (window as any).rdt
        ) {
          try {
            (window as any).rdt("init", "a2_ereu6m8fieh6", {
              externalId: statistic.player.uid,
              email: statistic.player.email,
            });
            redditUserInitRef.current = true;
          } catch (error) {
            console.error("Error initializing Reddit pixel for player:", error);
          }
        }
        if (
          statistic.isNewSignUp &&
          statistic.player &&
          typeof window !== "undefined" &&
          (window as any).rdt
        ) {
          try {
            (window as any).rdt("track", "SignUp");
          } catch (_) {}
        }

        try {
          const gtag = (window as any).gtag;
          if (typeof window !== "undefined" && gtag) {
            if (statistic.isNewSignUp && newPlayer) {
              gtag("event", "sign_up", { method: "Google" });
            }
            if (newPlayer && !prevPlayer) {
              gtag("event", "login", { method: "Google" });
            }
            if (newPlayer && prevPlayer) {
              const prevPoints = prevPlayer.points ?? 0;
              const prevSparks = prevPlayer.sparks ?? 0;
              const newPoints = newPlayer.points ?? 0;
              const newSparks = newPlayer.sparks ?? 0;
              if (newPoints > prevPoints) {
                gtag("event", "earn_virtual_currency", {
                  virtual_currency_name: "points",
                  value: newPoints - prevPoints,
                });
              } else if (newPoints < prevPoints) {
                gtag("event", "spend_virtual_currency", {
                  virtual_currency_name: "points",
                  value: prevPoints - newPoints,
                });
              }
              if (newSparks > prevSparks) {
                gtag("event", "earn_virtual_currency", {
                  virtual_currency_name: "sparks",
                  value: newSparks - prevSparks,
                });
              } else if (newSparks < prevSparks) {
                gtag("event", "spend_virtual_currency", {
                  virtual_currency_name: "sparks",
                  value: prevSparks - newSparks,
                });
              }
            }
          }
        } catch (_) {}
      }
    };

    const updateData = async () => {
      try {
        await fetchStatistic();
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    const intervalId = setInterval(updateData, 10000);
    updateData();

    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="App p-2">
      {isCrawler() && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 2147483647,
            background: "#000",
            color: "#666",
            fontSize: 10,
            padding: "4px 8px",
          }}
        >
          crawler
        </div>
      )}
      <MenuTop />
      <World />
      <ZoomController />
      <MiniMap />
      <MenuBottom />
      {state.isMostViewedPopupOpen && <RarityRatingPopup />}
      {state.isHexInfoPopupOpen && <CardPopup />}
      <PlayerProfilePopup />
      <LoginPopup />
      <GeneratePopup />
      {state.isPlayerInfoPopupOpen && !state.isHexInfoPopupOpen && <PlayerInfoPopup />}
      {state.isAboutGamePopupOpen && <AboutGamePopup />}
      {state.isGeneratedAwardPopupOpen && <GenerationFinishedPopup />}
      {state.isRedeemPopupOpen && <RedeemPopup />}
      {state.isReportPopupOpen && <ReportPopup />}
      <SystemMessage />
      {state.isMenuPopupOpen && <MenuPopup />}
      {state.isGlobalChatPopupOpen && <GlobalChatPopup />}
      {state.isSearchPopupOpen && <SearchPopup />}
      {state.isNotificationsPopupOpen && <NotificationsPopup />}
      {state.isBattleFieldPopupOpen && <BattleFieldPopup />}
      <MenuLeft />
    </div>
  );
}

export default App;
